import { useLocation } from 'react-router-dom';
import { useHubFrame, useHubWideGutterFrame } from '../../theaWeb/hub/HubFrameContext';
import { HubPageSkeleton } from '../../theaWeb/hub/HubPageSkeleton';
import React from 'react';
import styled from 'styled-components';
import { shimmerFill } from '../../motion';
import { SiteHeader } from './SiteHeader';
import { useDesktopNav } from '../../theaWeb/hooks/useDesktopNav';
import type { SiteHeaderNavItem } from './SiteNavLinks';

/**
 * Suspense fallback for lazy route chunks (header/IA spec v5 PR 2).
 *
 * Replaces the old `fallback={null}` — which rendered a blank white page
 * for the whole chunk download — with the Airbnb-style loading state:
 * brand chrome (real SiteHeader, wordmark + nav + avatar) is instant and
 * IDENTICAL to what the destination page renders, and only the content
 * area is a skeleton. When the chunk lands, the header doesn't move — the
 * skeleton body swaps for real content.
 *
 * The nav links come from the same useDesktopNav the real pages use;
 * with the has-boards hint (hasBoardsHint.ts) they render on the FIRST
 * frame for returning board-users, so there's no link pop-in between the
 * fallback and the mounted page.
 *
 * Bounded by construction: a Suspense fallback unmounts the moment the
 * chunk resolves (architecture.md "bounded, shape-identical" rule).
 *
 * Split container/view per repo story convention: the View is pure
 * (storied + Happo'd); only the container touches useDesktopNav.
 *
 * Shape: sentence-case title bar over a 2/3/4-column 4:5 tile grid —
 * the common silhouette of the content surfaces (home grid, boards,
 * guides, liked). Visual language mirrors HomeSkeleton / BoardSkeleton:
 * one shimmer style across every loading surface.
 */

const Page = styled.div`
  min-height: 100vh;
  background: hsl(var(--background));
  font-family: ${({ theme }) => theme.font.sans};
`;

const Content = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding: 8px 16px 40px;
  @media (min-width: 640px) {
    padding: 8px 32px 40px;
  }
  @media (min-width: 1024px) {
    padding: 8px 64px 40px;
  }
`;

// Sentence-case section-header stand-in (21px desktop / 19px mobile spec).
const TitleBar = styled.div`
  width: 180px;
  height: 22px;
  border-radius: 8px;
  margin: 10px 0 18px;
  ${shimmerFill};
`;

const Grid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, 1fr);
  @media (min-width: 640px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const Tile = styled.div`
  aspect-ratio: 4 / 5;
  border-radius: 16px;
  ${shimmerFill};
`;

export interface RouteShellFallbackViewProps {
  navItems: SiteHeaderNavItem[];
  /** Story/test override for SiteHeader's actions slot (the account menu).
   *  Omit for the real default (<HeaderAccountMenu />). */
  actions?: React.ReactNode;
}

/** Pure shell: real SiteHeader over a shimmer content skeleton. */
export const RouteShellFallbackView: React.FC<RouteShellFallbackViewProps> = ({
  navItems,
  actions,
}) => (
  <Page data-testid="route-shell-fallback">
    <SiteHeader navItems={navItems} actions={actions} />
    {/* Placeholder content is decoration, not information. */}
    <Content aria-hidden="true">
      <TitleBar />
      <Grid>
        {Array.from({ length: 8 }, (_, i) => (
          <Tile key={i} />
        ))}
      </Grid>
    </Content>
  </Page>
);

/** Container wired to the live nav — what App.js hands to Suspense. */
export const RouteShellFallback: React.FC = () => {
  const navItems = useDesktopNav();
  const hubFrame = useHubFrame();
  const wideGutterEnabled = useHubWideGutterFrame();
  const { pathname } = useLocation();
  if (hubFrame) return <HubPageSkeleton page={pathname.startsWith('/guide/') ? 'guide' : pathname.startsWith('/gift-guides/') ? 'collection' : pathname === '/gift-guides' ? 'hub' : pathname === '/calendar' ? 'calendar' : pathname === '/you' ? 'you' : 'people'} wideGutterEnabled={wideGutterEnabled} />;
  return <RouteShellFallbackView navItems={navItems} />;
};
